import React, { createContext, useContext, useState, useCallback } from "react";
import { Vocabulary } from "./Vocabulary";

interface AIProcessingContextType {
  isProcessing: boolean;
  message: string | null;
  startProcessing: (message?: string | null) => void;
  stopProcessing: () => void;
}

const AIProcessingContext = createContext<AIProcessingContextType | null>(null);



interface AIProcessingOverlayProps {
  message?: string | null;
}


/** Displays a message indicating that AI processing is underway. */
export const AIProcessingOverlay: React.FC<AIProcessingOverlayProps> = ({ message }) => {
  const displayMessage = message ?? Vocabulary.processingVehicleData;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(4px)",
      }}
    >
      {/* Center card */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 16,
          padding: 24,
          borderRadius: 12,
          backgroundColor: "rgba(255, 255, 255, 0.15)",
        }}
      >
        {/* Spinner */}
        <div
          style={{
            width: 64,
            height: 64,
            border: "4px solid #e5e7eb",
            borderTopColor: "#FF6633",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />

        {/* Message */}
        <p
          style={{
            color: "white",
            textAlign: "center",
            fontSize: 16,
            fontWeight: 500,
            margin: 0,
          }}
        >
          {displayMessage}
        </p>
      </div>

      {/* Keyframes for spinner animation */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};


/** Provides access to AI processing state and controls. */
export const useAIProcessing = () => {
  const context = useContext(AIProcessingContext);
  if (!context) {
    throw new Error("useAIProcessing must be used within AIProcessingProvider");
  }
  return context;
};

interface AIProcessingProviderProps {
  children: React.ReactNode;
}

/** Provides AI processing state and controls to its descendants. */
export const AIProcessingProvider: React.FC<AIProcessingProviderProps> = ({ children }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const startProcessing = useCallback((msg?: string | null) => {
    setMessage(msg ?? null);
    setIsProcessing(true);
  }, []);

  const stopProcessing = useCallback(() => {
    setIsProcessing(false);
    setMessage(null);
  }, []);

  return (
    <AIProcessingContext.Provider value={{ isProcessing, message, startProcessing, stopProcessing }}>
      {children}

      {/* Show overlay only when processing */}
      {isProcessing && <AIProcessingOverlay message={message} />}
    </AIProcessingContext.Provider>
  );
};
